import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Api } from 'Utils/api';
import { areEqual } from 'Utils/equalityChecks';
import { SignaturePad } from './SignaturePad';
import { FieldInputOverlay } from './FieldInputOverlay';
import { isValidNumericFieldValue } from './numericFieldUtils';
import { PdfFormSubmission } from '../PdfFormTemplates/types';
import styles from './pdfFormSign.module.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

function getFieldValueKey(field: {
  id?: number;
  field_name: string;
  field_type: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}) {
  if (field.id !== undefined) return `id:${field.id}`;
  return `tmp:${field.page}:${field.field_type}:${field.x}:${field.y}:${field.width}:${field.height}:${field.field_name}`;
}

function getMarkFieldTitle(
  fieldType: 'signature' | 'initial',
  index: number,
  total: number
): string {
  const base = fieldType === 'signature' ? 'Signature' : 'Initials';
  return total > 1 ? `${base} ${index + 1}` : `Your ${base}`;
}

function PdfFormSign() {
  const { uuid } = useParams<{ uuid: string }>();

  const [submission, setSubmission] = useState<PdfFormSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markValuesByKey, setMarkValuesByKey] = useState<Record<string, string | null>>({});
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);

  // PDF viewer state
  const [numPages, setNumPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [pageHeight, setPageHeight] = useState(0);
  const [pageWidth, setPageWidth] = useState(0);
  const [fieldValuesByKey, setFieldValuesByKey] = useState<Record<string, string | boolean | number>>({});
  const [focusedFieldKey, setFocusedFieldKey] = useState<string | null>(null);
  const markSectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Track window resize for responsive PDF width (including iOS visual viewport)
  useEffect(() => {
    const handleResize = () => {
      const vv = window.visualViewport;
      // Ignore pinch-zoom resize events so PDF width doesn't fight browser zoom.
      if (vv && Math.abs(vv.scale - 1) > 0.01) return;
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    // iOS Safari: visual viewport can change independently (keyboard, zoom)
    const vv = window.visualViewport;
    if (vv) vv.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (vv) vv.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fetch submission on mount
  useEffect(() => {
    if (!uuid) return;

    (async () => {
      try {
        // Ensure CSRF cookie is set
        await Api.csrfHeader();
        const { data } = await Api.get(`/pdf-form-sign/${uuid}`);
        if (data?.data) {
          setSubmission(data.data);
          if (data.data.status === 'signed') {
            setSigned(true);
          }
        } else {
          setError('Submission not found.');
        }
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 404) {
          setError('This form could not be found. It may have been deleted or the link is invalid.');
        } else {
          setError('Something went wrong. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [uuid]);

  // Pre-populate field values from resolved token values and saved field values
  useEffect(() => {
    if (!submission?.template?.fields) return;
    const tokenValues = submission.resolved_token_values || {};
    const savedValues = submission.field_values || {};
    const merged = { ...tokenValues, ...savedValues };
    if (Object.keys(merged).length === 0) return;

    const initial: Record<string, string | boolean | number> = {};
    const today = new Date().toISOString().slice(0, 10);
    submission.template.fields.forEach((field) => {
      const prefilled = merged[field.field_name];
      if (prefilled !== undefined && prefilled !== null && prefilled !== '') {
        if (field.field_type === 'checkbox') {
          initial[getFieldValueKey(field)] = prefilled === true || prefilled === '1' || prefilled === 'true' || prefilled === 'yes';
        } else {
          initial[getFieldValueKey(field)] = String(prefilled);
        }
      } else if (field.field_type === 'date' && field.field_name === '{{current_date}}') {
        initial[getFieldValueKey(field)] = today;
      }
    });
    if (Object.keys(initial).length > 0) {
      setFieldValuesByKey((prev) => ({ ...initial, ...prev }));
    }
  }, [submission]);

  const onMarkChange = useCallback((fieldKey: string, data: string | null) => {
    setMarkValuesByKey((prev) => ({ ...prev, [fieldKey]: data }));
  }, []);

  const onPageLoad = useCallback((page: any) => {
    setPageHeight(page.originalHeight);
    setPageWidth(page.originalWidth);
  }, []);

  const allFields = submission?.template?.fields || [];
  const isTokenField = useCallback((name: string) => /^\{\{.+\}\}$/.test(name), []);
  const isClientField = useCallback(
    (f: { field_name: string; field_type: string; user_fillable?: boolean }) => {
      if (f.field_type === 'signature' || f.field_type === 'initial') return false;
      const fillable = f.user_fillable == null || !!f.user_fillable;
      // Date fields are client-fillable when explicitly marked or when set to user_date
      if (f.field_type === 'date') return fillable || f.field_name === 'user_date';
      // Token fields ({{...}}) are only client-fillable when explicitly marked
      if (isTokenField(f.field_name)) return !!f.user_fillable;
      return fillable;
    },
    [isTokenField]
  );
  const fillableFields = allFields.filter((f) => f.page === currentPage && isClientField(f));
  const hasFields = allFields.some(isClientField);

  const isMobile = windowWidth < 600;
  // Account for container padding: content (8px*2) + card (12px*2) on mobile, content (24px*2) + card (24px*2) on desktop
  const renderWidth = isMobile
    ? windowWidth - 40 // 8+12 padding each side
    : Math.min(800, windowWidth - 96); // 24+24 padding each side
  const effectiveScale = pageWidth > 0 ? renderWidth / pageWidth : 1;

  const markFields = allFields
    .filter((f) => f.field_type === 'signature' || f.field_type === 'initial')
    .sort((a, b) => a.page - b.page || b.y - a.y);
  const signatureFields = markFields.filter((f) => f.field_type === 'signature');
  const initialFields = markFields.filter((f) => f.field_type === 'initial');
  const missingSignatureFieldKeys = signatureFields
    .map((field) => getFieldValueKey(field))
    .filter((fieldKey) => !markValuesByKey[fieldKey]);
  const missingInitialFieldKeys = initialFields
    .map((field) => getFieldValueKey(field))
    .filter((fieldKey) => !markValuesByKey[fieldKey]);
  const signatureRequired = signatureFields.length > 0;
  const initialsRequired = initialFields.length > 0;

  const requiredFields = allFields.filter((f) => f.required && isClientField(f));

  const getMissingRequiredFields = useCallback(() => requiredFields.filter((field) => {
    const key = getFieldValueKey(field);
    const val = fieldValuesByKey[key];
    if (val === undefined || val === '' || (typeof val === 'string' && val.trim() === '')) return true;
    if (field.field_type === 'checkbox' && val === false) return true;
    if (field.field_type === 'number' && !isValidNumericFieldValue(val)) return true;
    return false;
  }), [requiredFields, fieldValuesByKey]);

  const onNextRequired = useCallback(() => {
    const missing = getMissingRequiredFields();
    if (missing.length === 0) {
      const nextMissingMarkKey = missingSignatureFieldKeys[0] || missingInitialFieldKeys[0];
      if (nextMissingMarkKey) {
        markSectionRefs.current[nextMissingMarkKey]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    // Sort by page then vertical position so we go top-to-bottom, page-by-page
    // PDF coordinates have origin at bottom-left, so higher Y = higher on page
    missing.sort((a, b) => a.page - b.page || b.y - a.y);
    const target = missing[0];
    const targetKey = getFieldValueKey(target);
    if (target.page !== currentPage) {
      setCurrentPage(target.page);
    }
    // Toggle key off/on to re-trigger focus even if same field
    setFocusedFieldKey(null);
    setTimeout(() => setFocusedFieldKey(targetKey), 50);
  }, [currentPage, getMissingRequiredFields, missingInitialFieldKeys, missingSignatureFieldKeys]);

  // All fillable fields sorted by page then top-to-bottom
  const sortedFillableFields = allFields.filter(isClientField).sort((a, b) => a.page - b.page || b.y - a.y);

  const advanceFromField = useCallback(
    (currentFieldKey: string) => {
      const idx = sortedFillableFields.findIndex((f) => getFieldValueKey(f) === currentFieldKey);
      if (idx < 0 || idx >= sortedFillableFields.length - 1) return;
      const next = sortedFillableFields[idx + 1];
      const nextKey = getFieldValueKey(next);
      if (next.page !== currentPage) {
        setCurrentPage(next.page);
      }
      setFocusedFieldKey(null);
      setTimeout(() => setFocusedFieldKey(nextKey), 50);
    },
    [sortedFillableFields, currentPage]
  );

  const missingFieldCount = getMissingRequiredFields().length;
  const missingMarkCount = missingSignatureFieldKeys.length + missingInitialFieldKeys.length;
  const markCount = signatureFields.length + initialFields.length;
  const missingCount = missingFieldCount + missingMarkCount;
  const totalRequiredCount = requiredFields.length + markCount;
  const filledRequiredCount = totalRequiredCount - missingCount;

  const onSubmit = useCallback(async () => {
    if (!uuid) return;

    const missing = getMissingRequiredFields();
    if (missing.length > 0) {
      setError('Please fill in all required fields before submitting.');
      onNextRequired();
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const payload: any = {};
      if (hasFields) {
        const fillableAllPages = allFields.filter(isClientField);
        const fieldValues: Record<string, string | boolean | number> = {};
        const fieldValuesById: Record<string, string | boolean | number> = {};

        fillableAllPages.forEach((field) => {
          const fieldKey = getFieldValueKey(field);
          if (!(fieldKey in fieldValuesByKey)) return;
          const value = fieldValuesByKey[fieldKey];
          fieldValues[field.field_name] = value;
          if (field.id !== undefined) fieldValuesById[String(field.id)] = value;
        });

        payload.field_values = fieldValues;
        if (Object.keys(fieldValuesById).length > 0) payload.field_values_by_id = fieldValuesById;
      }
      const markValuesById: Record<string, string> = {};
      markFields.forEach((field) => {
        if (field.id === undefined) return;
        const fieldKey = getFieldValueKey(field);
        const value = markValuesByKey[fieldKey];
        if (!value) return;
        markValuesById[String(field.id)] = value.replace(/^data:image\/\w+;base64,/, '');
      });
      if (Object.keys(markValuesById).length > 0) {
        payload.mark_values_by_id = markValuesById;
      }
      const { data } = await Api.post(`/pdf-form-sign/${uuid}/sign`, payload);
      if (data?.data) {
        setSubmission(data.data);
        setSigned(true);
      }
    } catch (err: any) {
      const respData = err?.response?.data;
      let errorMsg = respData?.message || 'Failed to submit. Please try again.';
      // Show which fields failed validation if the backend provides details
      const errors = respData?.errors;
      if (errors && typeof errors === 'object') {
        const details = Object.entries(errors)
          .map(([key, msgs]) => `${key}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
          .join('\n');
        if (details) errorMsg += `\n${details}`;
      }
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  }, [allFields, fieldValuesByKey, getMissingRequiredFields, hasFields, isClientField, markFields, markValuesByKey, onNextRequired, uuid]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading form...</div>
      </div>
    );
  }

  if (error && !submission) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  if (!submission) return null;

  const pdfUrl = signed ? submission.signed_url : submission.prefilled_url;
  const companyName = submission.company?.name || '';

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerInner}>
          <h1>{submission.template?.name || 'Document'}</h1>
          {companyName && (
            <p>
              {`From ${companyName}`}
            </p>
          )}
        </div>
      </div>

      {/* Progress bar for required fields */}
      {submission.status === 'pending' && !signed && totalRequiredCount > 0 && (
        <div className={styles.progressBar}>
          <div className={styles.progressBarInner}>
            <div className={styles.progressText}>
              <span className={styles.progressCount}>{`${filledRequiredCount} / ${totalRequiredCount}`}</span>
              {' required fields completed'}
            </div>
            <div className={styles.progressTrack}>
              <div
                className={styles.progressFill}
                style={{ width: `${totalRequiredCount > 0 ? (filledRequiredCount / totalRequiredCount) * 100 : 0}%` }}
              />
            </div>
            {missingCount > 0 && (
              <button type="button" className={styles.progressNextBtn} onClick={onNextRequired}>
                Next
              </button>
            )}
          </div>
        </div>
      )}

      <div className={styles.content}>
        {/* Status banners */}
        {submission.status === 'signed' && (
          <div className={`${styles.statusBanner} ${styles.statusSigned}`}>
            {submission.signed_at
              ? `This document has been signed on ${new Date(submission.signed_at).toLocaleDateString()}.`
              : 'This document has been signed.'}
          </div>
        )}
        {submission.status === 'void' && (
          <div className={`${styles.statusBanner} ${styles.statusVoid}`}>
            This document has been voided and is no longer valid.
          </div>
        )}

        {/* PDF Preview */}
        {pdfUrl && (
          <div className={styles.card}>
            <h2>Document Preview</h2>
            <div className={styles.pdfPreview}>
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages: total }) => setNumPages(total)}
                loading={<div>Loading PDF...</div>}
              >
                <div style={{ position: 'relative', display: 'inline-block', overflow: 'visible' }}>
                  <Page
                    pageNumber={currentPage}
                    scale={1}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    width={renderWidth}
                    onLoadSuccess={onPageLoad}
                  />
                  {submission.status === 'pending' && !signed && fillableFields.map((field) => {
                    const fieldValueKey = getFieldValueKey(field);
                    const val = fieldValuesByKey[fieldValueKey] ?? (field.field_type === 'checkbox' ? false : '');
                    const isEmpty = val === '' || val === false || val === undefined;
                    const isInvalidNumber = field.field_type === 'number' && !isEmpty && !isValidNumericFieldValue(val);
                    return (
                    <FieldInputOverlay
                      key={fieldValueKey}
                      field={field}
                      fieldValueKey={fieldValueKey}
                      value={val}
                      scale={effectiveScale}
                      pageHeight={pageHeight}
                      onChange={(valueKey, v) => setFieldValuesByKey((prev) => ({ ...prev, [valueKey]: v }))}
                      showRequired={!!field.required && (isEmpty || isInvalidNumber) && !!error}
                      focused={focusedFieldKey === fieldValueKey}
                      onAdvance={() => advanceFromField(fieldValueKey)}
                    />
                    );
                  })}
                </div>
              </Document>
              {numPages > 1 && (
                <div className={styles.pdfNav}>
                  <button disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>
                    ← Previous
                  </button>
                  <span>
                    {`Page ${currentPage} of ${numPages}`}
                  </span>
                  <button disabled={currentPage >= numPages} onClick={() => setCurrentPage((p) => p + 1)}>
                    Next →
                  </button>
                </div>
              )}
            </div>

            {signed && pdfUrl && (
              <div style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  className={styles.viewPdfBtn}
                  onClick={() => {
                    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
                  }}
                >
                  Save PDF
                </button>
              </div>
            )}
          </div>
        )}

        {submission.status === 'pending' && !signed && signatureFields.map((field, index) => {
          const fieldKey = getFieldValueKey(field);
          return (
            <div
              key={fieldKey}
              className={styles.card}
              ref={(node) => {
                markSectionRefs.current[fieldKey] = node;
              }}
            >
              <h2>{getMarkFieldTitle('signature', index, signatureFields.length)}</h2>
              <SignaturePad
                label="Sign below"
                onSignatureChange={(data) => onMarkChange(fieldKey, data)}
              />
            </div>
          );
        })}

        {submission.status === 'pending' && !signed && initialFields.map((field, index) => {
          const fieldKey = getFieldValueKey(field);
          return (
            <div
              key={fieldKey}
              className={styles.card}
              ref={(node) => {
                markSectionRefs.current[fieldKey] = node;
              }}
            >
              <h2>{getMarkFieldTitle('initial', index, initialFields.length)}</h2>
              <SignaturePad
                label="Initial below"
                onSignatureChange={(data) => onMarkChange(fieldKey, data)}
              />
            </div>
          );
        })}

        {/* Submit button - shown when there are fields, signatures, or initials to submit */}
        {(hasFields || signatureRequired || initialsRequired) && submission.status === 'pending' && !signed && (
          <div className={styles.card}>
            {error && <pre style={{ color: '#dc3545', fontSize: 14, marginBottom: 12, textAlign: 'center', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{error}</pre>}
            <div className={styles.actions}>
              {missingCount > 0 && (
                <button
                  type="button"
                  className={styles.nextRequiredBtn}
                  onClick={onNextRequired}
                >
                  {`Next Required Field (${missingCount})`}
                </button>
              )}
              <button
                type="button"
                className={styles.submitBtn}
                onClick={onSubmit}
                disabled={submitting || missingMarkCount > 0}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        )}

        {/* Floating next required field button */}
        {submission.status === 'pending' && !signed && missingCount > 0 && (
          <button
            type="button"
            className={styles.floatingNextBtn}
            onClick={onNextRequired}
          >
            {`Next (${missingCount})`}
          </button>
        )}

        {/* Success message after signing */}
        {signed && submission.status === 'signed' && (
          <div className={styles.card}>
            <div className={styles.success}>
              <div className={styles.successIcon}>&#10003;</div>
              <h2>Submitted Successfully</h2>
              <p>Thank you! Your submission has been recorded.</p>
            </div>
          </div>
        )}

        {/* No signature or fields needed - just viewing */}
        {submission.status === 'pending' && !signatureRequired && !initialsRequired && !hasFields && (
          <div className={styles.card}>
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#6c757d' }}>
              This document is for your review. No signature is required.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const PdfFormSignMemo = memo(PdfFormSign, areEqual);
export { PdfFormSignMemo as PdfFormSign };
